import { partition } from 'es-toolkit';

import {
  FileManagerResult,
  getFullName,
  getNameFromMitekDesignFile,
  isAlpineJob,
  isMiTekJob,
  isSimpsonJob,
  preloadFiles,
  UploadedFile,
} from '@paragon/component-processor';
import {
  ComponentDesignCreationInput,
  DesignOrigin,
  Document,
  SealingServiceComponentDesign,
} from '@paragon/contract-library';
import { designService, sealingService } from '@paragon/data-connections';
import { hasSealsAndSignaturesPermission } from '@paragon/login-tools';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, getSpinnersToDisplay, removeSpinnerMessage } from '@paragon/task-spinner';

import { proceedOnlyIfJobIsUnlocked } from '../modules/jobLocking';
import { getCorrespondingLandscapeTemplate } from '../selectors/annotationTemplates';
import { confirmJobSeal } from '../store/annotations';
import { documentReplaced, documentsLoaded, jobReset, SanitizedDocument } from '../store/documents';
import { jobLoaded, jobLocked, jobUnlocked } from '../store/job';
import { flatten } from '../util';
import { savePageAnnotations } from './annotations';
import { loadDocuments, saveModifiedNotes, selectDocument, updateNotes } from './documents';
import { reindexPdfsNaturalSort } from './documentTools';
import { loadSeals } from './seals';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

const MITEK_STAMPING_TEMPLATE = 'Stamping-MiTek-Portrait';
const ALPINE_STAMPING_TEMPLATE = 'Stamping-Alpine-Portrait';
const PARAGON_STAMPING_TEMPLATE = 'Stamping-Paragon-Portrait';
const LANDSCAPE_TEMPLATE = 'StampingTemplateLandscape';

export const getDefaultPortraitStampingTemplate = (designOrigin: DesignOrigin) => {
  if (designOrigin === 'Alpine') {
    return ALPINE_STAMPING_TEMPLATE;
  }
  if (designOrigin === 'Paragon') {
    return PARAGON_STAMPING_TEMPLATE;
  }
  return MITEK_STAMPING_TEMPLATE;
};

export function loadJob(jobId: number): AsyncThunkActionVoid {
  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading job...'));

    const jobLoader = sealingService.jobs.fetch(jobId).then((job) => dispatch(jobLoaded(job)));
    const documentsLoader = dispatch(loadDocuments(jobId));

    try {
      await Promise.all([jobLoader, documentsLoader]);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error loading job.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

function createJobResetter(apiFunction: (currentJobId: number) => Promise<void>) {
  return (): AsyncThunkActionVoid => (dispatch, getState) => {
    const currentJobId = getState().job.jobId;

    const spinnerId = dispatch(addSpinnerMessage('Resetting job...'));
    dispatch(jobReset());

    return apiFunction(currentJobId)
      .then(() => dispatch(loadJob(currentJobId)))
      .catch((e) => console.error(e))
      .then(() => {
        dispatch(removeSpinnerMessage(spinnerId));
      });
  };
}

export const resetJob = createJobResetter(sealingService.jobs.reset);
export const resetSeals = createJobResetter(sealingService.jobs.resetSeals);

export function lockJob(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();
    if (!hasSealsAndSignaturesPermission(state)) {
      dispatch(enqueueWarning('You are not authorized to lock this job'));
      return;
    }

    if (!state.job.stampedByEmail) {
      dispatch(enqueueWarning('You can only lock a sealed job.'));
      return;
    }

    await sealingService.jobs.lock(state.job.jobId);
    dispatch(jobLocked());
  };
}

export function unlockJob(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();
    if (!hasSealsAndSignaturesPermission(state)) {
      dispatch(enqueueWarning('You are not authorized to unlock this job'));
      return;
    }

    await sealingService.jobs.unlock(state.job.jobId);

    dispatch(jobUnlocked());
  };
}

const SEALING_MESSAGE = 'Sealing job...';

export function sealJobDefaultSettings(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();

    // If job is being sealed, return
    if (getSpinnersToDisplay(state).has(SEALING_MESSAGE)) return;

    const { jobId } = state.job;

    const spinnerId = dispatch(addSpinnerMessage(SEALING_MESSAGE));

    await dispatch(savePageAnnotations());

    const defaultPortraitTemplate = getDefaultPortraitStampingTemplate(state.job.designOrigin);

    try {
      await sealingService.annotationTemplates.stampDrawingsAndCreateCoverAndBackPages(jobId, {
        restrictStampedPages: false,
        pagesToStamp: [],
        portraitTemplate: defaultPortraitTemplate,
        landscapeTemplate: LANDSCAPE_TEMPLATE,
        shouldCreateCoverPage: true,
        shouldCreateBackPage: true,
      });
      dispatch(confirmJobSeal());
    } catch (err) {
      console.error(err);
      dispatch(enqueueError('Error sealing job.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    const loadJobPromise = dispatch(loadJob(jobId));
    const loadSealsPromise = dispatch(loadSeals());
    await Promise.all([loadJobPromise, loadSealsPromise]);
  };
}

export function confirmSealJob(
  pdfPages: readonly number[],
  portraitTemplateId: number,
  createCoverPage: boolean,
  createBackPage: boolean,
): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const state = getState();
    const { jobId } = state.job;

    const portraitTemplate = state.annotationTemplates.annotationTemplates[portraitTemplateId].name;
    const landscapeTemplate =
      portraitTemplateId != null ? getCorrespondingLandscapeTemplate(portraitTemplate, state) : null;

    const spinnerId = dispatch(addSpinnerMessage(SEALING_MESSAGE));

    await dispatch(savePageAnnotations());

    try {
      await sealingService.annotationTemplates.stampDrawingsAndCreateCoverAndBackPages(jobId, {
        restrictStampedPages: true,
        pagesToStamp: pdfPages,
        shouldCreateCoverPage: createCoverPage,
        shouldCreateBackPage: createBackPage,
        portraitTemplate,
        landscapeTemplate,
      });
      dispatch(confirmJobSeal());
    } catch (err) {
      console.error(err);
      dispatch(enqueueError('Error sealing job.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    await dispatch(loadJob(jobId));
  };
}

// Returns a list of names of corrupt PDFs
export const addMetaPdf =
  (pdfsToAdd: readonly UploadedFile[], selectAfterAdding = true): AsyncThunkActionReturn<readonly string[]> =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding PDF...'));

    const currentState = getState();
    const activeDocumentIndex =
      currentState.documents.activeDocumentId == null
        ? -1
        : currentState.documents.pdfData[currentState.documents.activeDocumentId].index;

    try {
      const { addedPdfs, corruptPdfs } = await sealingService.jobs.addMetaPdf(
        currentState.job.jobId,
        pdfsToAdd.map((pdf, i) => ({
          name: `${pdf.filename}.pdf`,
          index: activeDocumentIndex + 1 + i,
          url: pdf.contents,
        })),
      );

      dispatch(documentsLoaded({ documents: addedPdfs, newlyCreated: true }));
      if (selectAfterAdding) dispatch(selectDocument(addedPdfs[0].id));

      return corruptPdfs;
    } catch {
      dispatch(enqueueError('Error adding other PDFs.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    return [];
  };

export function addMiscellaneousFiles(miscFiles: readonly UploadedFile[]): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding miscellaneous files...'));

    const { jobId } = getState().job;

    try {
      await sealingService.jobs.addMiscellaneousFiles(
        jobId,
        miscFiles.map((file) => ({
          name: getFullName(file),
          url: file.contents,
        })),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

interface OrchestrateAddingParagonTddSuccess {
  readonly success: true;
  readonly addedComponent: SealingServiceComponentDesign;
}

interface OrchestrateAddingParagonTddFailure {
  readonly success: false;
  readonly componentName: string | undefined;
}

type OrchestrateAddingParagonTddResult = OrchestrateAddingParagonTddSuccess | OrchestrateAddingParagonTddFailure;

const orchestrateAddingParagonTdd =
  (componentToAdd: PreparedFileManagerComponent): AsyncThunkActionReturn<OrchestrateAddingParagonTddResult | null> =>
  async (dispatch, getState) => {
    const treFile = componentToAdd.componentFiles.find((file) => file.filetype!.toLowerCase() === 'tre');
    const ansFile = componentToAdd.componentFiles.find((file) => file.filetype!.toLowerCase() === 'ans');

    if (!(treFile && ansFile)) {
      dispatch(enqueueError(`MiTek files needed for ${componentToAdd.name}. Continuing.`));
      return Promise.resolve(null);
    }

    const state = getState();

    const componentDesignCreationInput: ComponentDesignCreationInput = {
      jobId: state.job.jobId,
      organization: state.job.ownerOrganization,
      treFile: treFile.contents!,
      ansFile: ansFile.contents!,
      treFileName: treFile.filename,
      ansFileName: ansFile.filename,
      plateManufacturer: state.job.plateManufacturer!,
      convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo:
        state.job.convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo,
    };

    function handleError(userVisibleExceptionMessage?: string) {
      dispatch(
        enqueueError(
          `Error adding ${componentToAdd.name}${
            userVisibleExceptionMessage === undefined ? '' : `: ${userVisibleExceptionMessage}`
          }. Continuing.`,
        ),
      );
      console.error(`Error adding ${componentToAdd.name}.`);
    }

    try {
      const insertFromComponentDesignCreationInputResult =
        await designService.componentDesigns.postFromComponentDesignComponentDesignCreationInput(
          componentDesignCreationInput,
        );
      if (insertFromComponentDesignCreationInputResult.result === 'Exception') {
        handleError(
          insertFromComponentDesignCreationInputResult.isUserVisibleException
            ? insertFromComponentDesignCreationInputResult.exception
            : undefined,
        );
        console.error(insertFromComponentDesignCreationInputResult.exception);
        return Promise.resolve({ success: false, componentName: componentToAdd.name });
      }
      const analyzeByComponentDesignGuidResult = await designService.componentDesigns.postAnalyzeByComponentDesignGuid(
        insertFromComponentDesignCreationInputResult.componentDesignGuid,
        { commitName: 'Creating Paragon TDD', abbreviated: true, logVariables: false },
      );
      if (analyzeByComponentDesignGuidResult.result === 'Exception') {
        handleError(
          analyzeByComponentDesignGuidResult.isUserVisibleException
            ? analyzeByComponentDesignGuidResult.exception
            : undefined,
        );
        console.error(analyzeByComponentDesignGuidResult.exception);
        return Promise.resolve({ success: false, componentName: componentToAdd.name });
      }
      const addedComponent = await designService.componentDesigns.postCreateTddInJob(
        analyzeByComponentDesignGuidResult.analysisReportGuid,
        state.job.id,
      );

      return { success: true, addedComponent };
    } catch {
      handleError();
      return Promise.resolve({ success: false, componentName: componentToAdd.name });
    }
  };

const generateParagonTdds =
  (
    componentsToAdd: readonly PreparedFileManagerComponent[],
    selectAfterAdding = true,
  ): AsyncThunkActionReturn<readonly (string | undefined)[]> =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Adding components...'));

    let failedComponents: readonly (string | undefined)[] = [];

    const state = getState();
    try {
      const addingComponentsPromises = componentsToAdd.map((component) =>
        dispatch(orchestrateAddingParagonTdd(component)),
      );
      const addingComponentsResults = await Promise.all(addingComponentsPromises);
      const partitionedResults = partition(
        // TODO Handle null?
        addingComponentsResults as readonly OrchestrateAddingParagonTddResult[],
        (result): result is OrchestrateAddingParagonTddSuccess => result.success,
      );
      const addedComponents = partitionedResults[0].map((result) => result.addedComponent);
      failedComponents = partitionedResults[1].map((result) => result.componentName);

      const getAddedDocumentsPromises = addedComponents.map((addedComponent) =>
        sealingService.jobs.getDocument(addedComponent.pdfFileId!),
      );
      const addedDocuments = await Promise.all(getAddedDocumentsPromises);
      dispatch(documentsLoaded({ documents: addedDocuments, newlyCreated: true }));

      for (const addedDocument of addedDocuments) {
        dispatch(
          updateNotes(
            addedDocument.id,
            `Added by ${state.logInData.loggedInUser!.firstName} ${state.logInData.loggedInUser!.lastName}.`,
          ),
        );
      }
      await dispatch(saveModifiedNotes());

      if (selectAfterAdding) dispatch(selectDocument(addedDocuments[0].id));
    } catch {
      dispatch(enqueueError('Error adding Paragon component designs.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    return failedComponents;
  };

// Returns a list of names of components with corrupt PDFs
const addComponents =
  (
    componentsToAdd: readonly PreparedFileManagerComponent[],
    selectAfterAdding = true,
  ): AsyncThunkActionReturn<readonly (string | undefined)[]> =>
  async (dispatch, getState) => {
    const state = getState();

    if (state.job.designOrigin === 'Paragon') {
      const failedComponents = await dispatch(generateParagonTdds(componentsToAdd, selectAfterAdding));
      return failedComponents;
    }

    const spinnerId = dispatch(addSpinnerMessage('Adding components...'));

    dispatch(savePageAnnotations());

    const componentsToUpload = componentsToAdd.map((component) => formatComponentForUpload(component, state.job.jobId));

    try {
      const { addedComponents, corruptComponents } =
        await sealingService.componentDesigns.createMultiple(componentsToUpload);
      const getAddedDocumentsPromises = addedComponents.map((addedComponent) =>
        sealingService.jobs.getDocument(addedComponent.pdfFileId!),
      );
      const addedDocuments = await Promise.all(getAddedDocumentsPromises);

      dispatch(documentsLoaded({ documents: addedDocuments, newlyCreated: true }));

      if (selectAfterAdding) dispatch(selectDocument(addedDocuments[0].id));

      return corruptComponents;
    } catch {
      dispatch(enqueueError('Error adding component designs.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    return [];
  };

export function addDocuments(fileManagerResult: FileManagerResult): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const spinnerId = dispatch(addSpinnerMessage('Uploading new items...'));

    let corruptDocumentNames: readonly (string | undefined)[] = [];

    try {
      const componentDesigns = Object.entries(fileManagerResult.componentDesigns);
      for (const componentBatch of batchArray(componentDesigns)) {
        const preparedComponents = await prepareFileManagerComponents(componentBatch);
        const corruptComponents = await dispatch(
          addComponents(preparedComponents, getState().documents.pdfCount === 0),
        );
        corruptDocumentNames = corruptDocumentNames.concat(corruptComponents);
      }

      const metaPdfs = Object.values(fileManagerResult.metaPdfs);
      for (const metaPdfBatch of batchArray(metaPdfs)) {
        await preloadFiles(metaPdfBatch);
        const corruptPdfs = await dispatch(addMetaPdf(metaPdfBatch, getState().documents.pdfCount === 0));
        corruptDocumentNames = corruptDocumentNames.concat(corruptPdfs);
      }

      const miscFiles = Object.values(fileManagerResult.miscellaneousFiles);
      for (const miscFileBatch of batchArray(miscFiles)) {
        await preloadFiles(miscFileBatch);
        await dispatch(addMiscellaneousFiles(miscFileBatch));
      }

      await dispatch(reindexPdfsNaturalSort());
    } catch (e) {
      console.error(e);
      dispatch(enqueueError('Error uploading items.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    if (corruptDocumentNames.length > 0) {
      dispatch(
        enqueueWarning(
          `The following documents are corrupt and could not be added: ${corruptDocumentNames.join(', ')}`,
        ),
      );
    }
  };
}

interface PreparedFileManagerComponent {
  readonly name?: string;
  readonly componentFiles: readonly UploadedFile[];
}

async function prepareFileManagerComponents(
  components: readonly [string, readonly UploadedFile[]][],
): Promise<readonly PreparedFileManagerComponent[]> {
  await preloadFiles(flatten(components.map(([__, files]) => files)));

  return components.map(([name, componentFiles]) => ({ name, componentFiles }));
}

function batchArray<T>(source: readonly T[], batchSize = 10) {
  const batches = [];

  for (let i = 0; i < source.length; i += batchSize) {
    batches.push(source.slice(i, i + batchSize));
  }

  return batches;
}

export function replaceComponentDesigns(
  componentsToUpload: readonly SealingServiceComponentDesign[],
  existingComponentDocuments: readonly SanitizedDocument[],
): AsyncThunkActionVoid {
  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Replacing components...'));
    await dispatch(savePageAnnotations());

    for (const component of componentsToUpload) {
      let newDocument: Document;

      try {
        const newComponentDesign = await sealingService.componentDesigns.replace(component);
        newDocument = await sealingService.jobs.getDocument(newComponentDesign.pdfFileId!);
      } catch (exception) {
        if (exception === 'Corrupt PDF.') dispatch(enqueueError(`${component.name} is corrupt.`));
        else dispatch(enqueueError(`Error replacing files for ${component.name}.`));
        continue;
      }

      const correspondingOldDocument = existingComponentDocuments.find(
        (design) => design.componentDesignId === newDocument.componentDesignId,
      );

      dispatch(documentReplaced({ oldDocumentId: correspondingOldDocument!.id, newDocument }));

      if (component === componentsToUpload[0]) {
        dispatch(selectDocument(newDocument.id));
      }
    }

    dispatch(removeSpinnerMessage(spinnerId));
  };
}

interface DraftJobFile {
  readonly name: string;
  readonly url: string;
}

interface DraftPdf {
  readonly $type: string;
  index?: number;
  notes?: string;
}

interface DraftSealingServiceComponentDesign {
  name: string | undefined;
  jobId: number;
  designOrigin?: DesignOrigin;
  pdfFile?: DraftPdf;
  designFile?: DraftJobFile;
  analysisFile?: DraftJobFile;
  dxfFile?: DraftJobFile;
}

export function formatComponentForUpload(
  componentData: PreparedFileManagerComponent,
  jobId: number,
  oldIndex?: number,
  oldNotes?: string,
): SealingServiceComponentDesign {
  const result: DraftSealingServiceComponentDesign = {
    name: componentData.name,
    jobId,
  };

  const componentHasMitekFiles = isMiTekJob(componentData.componentFiles);
  const componentHasAlpineFiles = isAlpineJob(componentData.componentFiles);
  const componentHasSimpsonFiles = isSimpsonJob(componentData.componentFiles);
  const numberOfMatches = [componentHasMitekFiles, componentHasAlpineFiles, componentHasSimpsonFiles].filter(
    (x) => x,
  ).length;
  if (numberOfMatches > 1) {
    console.error(`Provided files for ${componentData.name} match more than one manufacturer.`);
  } else if (numberOfMatches === 0) {
    console.error(`${componentData.name} does not match a recognized manufacturer.`);
  } else if (componentHasMitekFiles) result.designOrigin = 'Mitek';
  else if (componentHasAlpineFiles) result.designOrigin = 'Alpine';
  else if (componentHasSimpsonFiles) result.designOrigin = 'Simpson';

  for (const fileData of componentData.componentFiles) {
    const componentFile = {
      name: `${getNameFromMitekDesignFile(fileData, componentData.componentFiles)}.${fileData.filetype}`,
      url: fileData.contents!,
    };

    switch (fileData.filetype!.toLowerCase()) {
      case 'pdf':
        result.pdfFile = {
          ...componentFile,
          $type: 'SealingServer.Models.Files.Pdf, SealingServer',
        };

        if (oldIndex) result.pdfFile.index = oldIndex;
        if (oldNotes) result.pdfFile.notes = oldNotes;

        break;
      case 'tre':
      case 'pic':
        result.designFile = componentFile;
        break;
      case 'ans':
      case 'eng':
        result.analysisFile = componentFile;
        break;
      case 'dxf':
      case 'dwg':
        result.dxfFile = componentFile;
        break;
      default:
        console.error(
          `Warning: encountered unrecognized file type '${fileData.filetype}' when preparing component for upload.`,
        );
        break;
    }
  }

  return result as SealingServiceComponentDesign;
}

export function rejectJob(): AsyncThunkActionVoid {
  return (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Rejecting job...'));

    return dispatch(saveModifiedNotes()).then(() => {
      const state = getState();
      return sealingService.jobs
        .rejectJob(state.job.jobId)
        .then(() => dispatch(removeSpinnerMessage(spinnerId)))
        .then(() => window.close())
        .catch((err) => console.error(err));
    });
  };
}

export function returnJob(): AsyncThunkActionReturn<boolean> {
  return async (dispatch, getState) => {
    const state = getState();
    const spinnerId = dispatch(addSpinnerMessage('Returning job...'));

    try {
      await dispatch(saveModifiedNotes());
      await dispatch(savePageAnnotations());

      await sealingService.jobs.returnJob(state.job.jobId);
    } catch (err) {
      console.error(err);
      dispatch(enqueueError(`Error Saving! Please Contact Paragon Support with Job ID: ${state.job.jobId}`));

      return false;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    return true;
  };
}
